import { useEffect, useMemo, useRef, useState } from "react";

const MAX_SIMULATED_PROGRESS = 96;
const UPDATE_INTERVAL_MS = 60;

const PDF_PREPARATION_PHASES = [
  { minimumProgress: 0, message: "Starting PDF preparation…" },
  { minimumProgress: 15, message: "Loading community indicator data…" },
  { minimumProgress: 35, message: "Calculating index classifications…" },
  { minimumProgress: 58, message: "Rendering metric cards and charts…" },
  { minimumProgress: 78, message: "Assembling PDF pages…" },
  { minimumProgress: 90, message: "Finalizing your PDF…" },
] as const;

const getProgressIncrement = (progress: number) => {
  if (progress < 25) return 3;
  if (progress < 50) return 2.5;
  if (progress < 72) return 1.8;
  if (progress < 88) return 1;
  return 0.25;
};

const PDFLoading = () => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(4);

  useEffect(() => {
    const previouslyFocusedElement = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    return () => {
      previouslyFocusedElement?.focus();
    };
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setProgress((currentProgress) => Math.min(
        currentProgress + getProgressIncrement(currentProgress),
        MAX_SIMULATED_PROGRESS,
      ));
    }, UPDATE_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, []);

  const roundedProgress = Math.round(progress);
  const currentStatus = useMemo(() => {
    return [...PDF_PREPARATION_PHASES]
      .reverse()
      .find((phase) => progress >= phase.minimumProgress)?.message
      ?? PDF_PREPARATION_PHASES[0].message;
  }, [progress]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-100 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pdf-loading-title"
      aria-describedby="pdf-loading-status"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-7 shadow-lg outline-none"
      >
        <div className="mb-5 text-center">
          <h1
            id="pdf-loading-title"
            className="text-lg font-semibold text-lacey-blue"
          >
            Preparing your PDF
          </h1>
          <p className="mt-1 text-xs text-slate-500">
            Please keep this window open.
          </p>
        </div>

        <div
          className="h-3 w-full overflow-hidden rounded-full bg-slate-200"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={roundedProgress}
          aria-valuetext={currentStatus}
        >
          <div
            className="h-full rounded-full bg-lacey-blue transition-[width] duration-500 ease-out motion-reduce:transition-none"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="mt-3 flex items-start justify-between gap-4">
          <p
            id="pdf-loading-status"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="text-sm font-medium text-slate-700"
          >
            {currentStatus}
          </p>
          <span className="shrink-0 text-sm font-semibold tabular-nums text-lacey-blue">
            {roundedProgress}%
          </span>
        </div>

        <p className="mt-5 text-center text-xs leading-5 text-slate-500">
          Larger selections and charts may take a little longer to process.
        </p>
      </div>
    </div>
  );
};

export default PDFLoading;